import { useState } from 'react'
import clsx from 'clsx'
import ScoreRing from 'shared-components/ui/ScoreRing'
import styles from './ScoreGauge.module.css'

interface ScoreGaugeProps {
  /** The ring's fill, on `max`. */
  value: number
  max: number
  /** Inside the ring: the figure (defaults to `value`) and the scale line (defaults to "/ max"). */
  valueText?: string
  maxText?: string
  /** Blue-cyan (the lab scores) or violet (owner figures — neutral, no good/bad colour meaning). */
  variant?: 'base' | 'violet'
  /** Caption under the ring; a long label ("Noise Isolation / ANC") clamps and expands on click. */
  label: string
  /** Small uppercase line under the label ("Noise cancelling", "Owner reported"). */
  sub?: string
  /** Names the ring for assistive tech, when the figure inside is not self-explanatory. */
  ringAriaLabel?: string
  className?: string
}

/**
 * One ring gauge + caption — the hero's featured scores ("8.2 / 10 · ANC") and the owner
 * dial ("2 of 2 · Buy again") are the same component, so the two read as one system.
 * The ring is the shared ScoreRing; a long label clamps to two lines and expands on click,
 * while the copy beside the gauge stays fully shown.
 */
const ScoreGauge: React.VFC<ScoreGaugeProps> = ({
  value,
  max,
  valueText,
  maxText,
  variant,
  label,
  sub,
  ringAriaLabel,
  className,
}) => {
  const [labelOpen, setLabelOpen] = useState(false)
  // Short labels ("Sound", "ANC") render plain; a long derived name clamps + expands.
  const labelIsLong = label.length > 12
  const ring = (
    <ScoreRing className={styles.ring} score={value} maxScore={Math.max(max, 1)} variant={variant}>
      <span className={styles.ringText}>
        <span className={styles.ringVal}>{valueText ?? value}</span>
        <span className={styles.ringMax}>{maxText ?? `/ ${max}`}</span>
      </span>
    </ScoreRing>
  )

  return (
    <div className={clsx(styles.gauge, className)}>
      {ringAriaLabel ? (
        <div role="img" aria-label={ringAriaLabel}>
          {ring}
        </div>
      ) : (
        ring
      )}
      {labelIsLong ? (
        <button
          type="button"
          className={clsx(styles.label, !labelOpen && styles.labelClamp)}
          data-button
          title={label}
          aria-expanded={labelOpen}
          onClick={() => setLabelOpen(o => !o)}
        >
          {label}
        </button>
      ) : (
        <span className={styles.label}>{label}</span>
      )}
      {sub && <span className={styles.sub}>{sub}</span>}
    </div>
  )
}

export default ScoreGauge
